import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { faRedo, faTrashCan, faSearch } from '@fortawesome/free-solid-svg-icons';
import Pagination from '../../components/Pagination';
import ConfirmationModal from '../../components/ConfirmationModal';
import TextInput from '../../components/TextInput';
import Button from '../../components/Button';
import SkeletonLoader from '../../components/SkeletonLoader';
import leadApi from '../../api/leadApi';
import { useToast } from '../../contexts/ToastContext';

const TrashedLeads = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { addToast } = useToast();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [itemsPerPage] = useState(10);

  const [isRestoreModalOpen, setIsRestoreModalOpen] = useState(false);
  const [isPermanentDeleteModalOpen, setIsPermanentDeleteModalOpen] = useState(false);
  const [leadToActOnId, setLeadToActOnId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [error, setError] = useState(null);

  const [lastPage, setLastPage] = useState(1);

  const queryParams = new URLSearchParams(location.search);
  const currentPage = parseInt(queryParams.get('page')) || 1;

  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchTerm]);

  const fetchTrashedLeads = useCallback(async (page, perPage, search) => {
    setLoading(true);
    setError(null);
    try {
      const response = await leadApi.getLeads({ page, perPage, search, includeTrashed: true });
      setLeads(response.data || []);
      setLastPage(response.pagination ? response.pagination.last_page : 1);
    } catch (err) {
      console.error('Failed to fetch trashed leads:', err);
      setError('Failed to load trashed leads. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTrashedLeads(currentPage, itemsPerPage, debouncedSearchTerm);
  }, [fetchTrashedLeads, currentPage, itemsPerPage, debouncedSearchTerm]);

  const handleRestoreClick = (id) => {
    setLeadToActOnId(id);
    setIsRestoreModalOpen(true);
  };

  const handleConfirmRestore = async () => {
    setIsRestoreModalOpen(false);
    if (leadToActOnId) {
      setLoading(true);
      setError(null);
      try {
        await leadApi.restoreLead(leadToActOnId);
        addToast('Lead restored successfully!', 'success');
        await fetchTrashedLeads(currentPage, itemsPerPage, debouncedSearchTerm);
      } catch (err) {
        console.error('Failed to restore lead:', err);
        addToast(err.response?.data?.message || 'Failed to restore lead. Please try again.', 'error');
        setError('Failed to restore lead. Please try again.');
      } finally {
        setLoading(false);
        setLeadToActOnId(null);
      }
    }
  };

  const handlePermanentDeleteClick = (id) => {
    setLeadToActOnId(id);
    setIsPermanentDeleteModalOpen(true);
  };

  const handleConfirmPermanentDelete = async () => {
    setIsPermanentDeleteModalOpen(false);
    if (leadToActOnId) {
      setLoading(true);
      setError(null);
      try {
        await leadApi.permanentDeleteLead(leadToActOnId);
        addToast('Lead permanently deleted successfully!', 'success');
        await fetchTrashedLeads(currentPage, itemsPerPage, debouncedSearchTerm);
      } catch (err) {
        console.error('Failed to permanently delete lead:', err);
        addToast(err.response?.data?.message || 'Failed to permanently delete lead. Please try again.', 'error');
        setError('Failed to permanently delete lead. Please try again.');
      } finally {
        setLoading(false);
        setLeadToActOnId(null);
      }
    }
  };

  const handleCancelAction = () => {
    setIsRestoreModalOpen(false);
    setIsPermanentDeleteModalOpen(false);
    setLeadToActOnId(null);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('page', 1);
    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  return (
    <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white flex flex-col min-h-[calc(100vh-100px)]">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-text-white">Trashed Contact Leads</h1>
        <Button
          onClick={() => navigate('/contact-leads')}
          className="bg-accent-red hover:bg-red-700 text-white"
        >
          Back to Contact Leads
        </Button>
      </div>

      <div className="mb-4">
        <TextInput
          label="Search Trashed Leads"
          id="search-trashed-leads"
          name="search-trashed-leads"
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder="Search by name, email, or subject..."
          icon={faSearch}
        />
      </div>

      {loading ? (
        <SkeletonLoader type="table" count={itemsPerPage} />
      ) : error ? (
        <div className="text-red-500 text-center py-4">{error}</div>
      ) : (
        <>
          <div className="overflow-x-auto flex-grow">
            <table className="min-w-full bg-gray-800 border border-gray-700">
              <thead>
                <tr>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">ID</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Name</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Email</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Subject</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Status</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Actions</th>
                </tr>
              </thead>
              <tbody>
                {leads.length > 0 ? (
                  leads.map((lead) => (
                    <tr key={lead.id} className="hover:bg-gray-700">
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">{lead.id}</td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">{lead.name}</td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">{lead.email}</td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">{lead.subject}</td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">
                        {lead.lead_status ? (
                          <span
                            className="px-2 py-1 rounded-full text-xs font-semibold"
                            style={{ backgroundColor: lead.lead_status.color || '#6B7280', color: '#FFFFFF' }}
                          >
                            {lead.lead_status.name}
                          </span>
                        ) : (
                          'N/A'
                        )}
                      </td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm flex items-center flex-nowrap">
                        <Button
                          onClick={() => handleRestoreClick(lead.id)}
                          className="text-green-500 hover:text-green-600 mr-3"
                          icon={faRedo}
                          tooltipId={`restore-lead-tooltip-${lead.id}`}
                          tooltipContent="Restore"
                        />
                        <Button
                          onClick={() => handlePermanentDeleteClick(lead.id)}
                          className="text-red-600 hover:text-red-700"
                          icon={faTrashCan}
                          tooltipId={`permanent-delete-lead-tooltip-${lead.id}`}
                          tooltipContent="Permanent Delete"
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="py-3 px-4 text-center text-sm text-text-gray">No trashed leads found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={lastPage}
          />
        </>
      )}

      <ConfirmationModal
        isOpen={isRestoreModalOpen}
        onClose={handleCancelAction}
        onConfirm={handleConfirmRestore}
        title="Confirm Restore"
        message="Are you sure you want to restore this lead?"
      />

      <ConfirmationModal
        isOpen={isPermanentDeleteModalOpen}
        onClose={handleCancelAction}
        onConfirm={handleConfirmPermanentDelete}
        title="Confirm Permanent Delete"
        message="Are you sure you want to permanently delete this lead? This action cannot be undone."
      />
    </div>
  );
};

export default TrashedLeads;